//@ts-check
import { useMemo } from 'react';

import { useStaticData } from '@exchange-frontends/custom-hooks';

import { CONSENT_STATUS_TO_APP_STATUS } from '../../../pages/manage/utils/Constants';
import useEcAppsListConsents from '../../entitlementApi/selectors/ecAppsList/useEcAppsListConsents';
import useEnterpriseEntitlements from '../../entitlementApi/selectors/ecAppsList/useEnterpriseEntitlements';
import useEcVersions from './useEcVersions';
import useXrAppBuilderApps from './useXrAppBuilderApps';

export const DEFAULT_ABD_OPTIONS = Object.freeze({
  appType: 'ABD',
  sort: 'CREATED',
  order: 'DESC',
  pageSize: 250,
});

/** @param {import('../../types').QueryOptions} options */
const useAppBuilderAppsList = (options = {}) => {
  const extensionPoints = useStaticData('extensionPoints');
  const xrAppBuilderAppsQuery = useXrAppBuilderApps(options);
  const entpQueryOptions = { ...DEFAULT_ABD_OPTIONS, ...options };
  const entitlementsQuery = useEnterpriseEntitlements(entpQueryOptions);
  const consentsQuery = useEcAppsListConsents(entpQueryOptions);
  const appVersionList = entitlementsQuery.data?.enterpriseEntitlements?.map(({ appId }) => ({ appId }));
  const versionsDetailsQuery = useEcVersions({
    appVersionList,
    type: 'details',
    ...options,
  });
  const queries = [entitlementsQuery, versionsDetailsQuery, xrAppBuilderAppsQuery];
  const isLoading = queries.some((query) => query.isLoading && query.isFetching);
  const error = queries.find((query) => query.error)?.error;

  const appBuilderApps = useMemo(() => {
    const entitlements = entitlementsQuery.data?.enterpriseEntitlements;
    if (isLoading || !entitlements?.length) return [];
    return entitlements.map((entitlement) => {
      const details = versionsDetailsQuery.data?.cloudVersions?.find((version) => version.appId === entitlement.appId);
      const consent = consentsQuery.data?.consents?.find((consent) => consent.appId === entitlement.appId);
      const hostApps = details?.hostApps || [];
      const optionalProducts = details?.optionalProducts || [];
      let isRetractedApp = false;
      if (details?.unpublishedDate && details?.publishedDate) {
        isRetractedApp = new Date(details.unpublishedDate) > new Date(details.publishedDate);
      }
      return {
        id: entitlement?.appId,
        status: CONSENT_STATUS_TO_APP_STATUS[consent?.status],
        name: details?.name || entitlement?.appId,
        isPrivate: details?.isPrivate,
        appVersionId: details?.id,
        eula: details?.eula,
        isRetractedApp,
        acquiredOn: entitlement?.createdOn,
        iconUrl: details?.assets?.find((asset) => asset?.type === 'icon' && asset?.order === 1)?.url,
        products: [...hostApps, ...optionalProducts],
        description: details?.description,
        extensionDetails: [details?.extensionType || 'App Builder'],
        jaeger: false,
        releaseNote: details?.releaseNote,
        details,
      };
    });
  }, [
    consentsQuery.data?.consents,
    entitlementsQuery.data?.enterpriseEntitlements,
    isLoading,
    versionsDetailsQuery.data?.cloudVersions,
  ]);

  const xrAppBuilderApps = useMemo(() => {
    const xrApps = xrAppBuilderAppsQuery.data?.apps;
    if (isLoading || !xrApps?.length) return [];
    return xrApps.map((details) => {
      let extensionDetails = details?.products?.map((product) => extensionPoints?.get(product?.name)).filter(Boolean);
      if (!extensionDetails?.length) {
        extensionDetails = ['App Builder'];
      }
      return {
        id: details?.id,
        status: details?.status,
        name: details?.title,
        isPrivate: true,
        appVersionId: null,
        eula: null,
        isRetractedApp: details?.status === 'RETRACTED',
        acquiredOn: details?.lastModifiedDatetime,
        iconUrl: details?.iconUrl,
        products: details?.products?.map((product) => product?.name),
        description: details?.description,
        extensionDetails,
        jaeger: details?.jaeger,
        releaseNote: details?.developersNotes,
        details,
      };
    });
  }, [xrAppBuilderAppsQuery.data?.apps, isLoading, extensionPoints]);

  const apps = useMemo(() => [...appBuilderApps, ...xrAppBuilderApps], [appBuilderApps, xrAppBuilderApps]);

  return {
    consentsQuery,
    entitlementsQuery,
    versionsDetailsQuery,
    xrAppBuilderAppsQuery,
    appBuilderApps,
    xrAppBuilderApps,
    apps,
    isLoading,
    error,
  };
};

export default useAppBuilderAppsList;
